import React from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import { useRouter } from 'next/router';

import { initObservability, useObservability } from '../helpers/observability';

type State = { hasError: boolean };
type Props = {
  observability: ReturnType<typeof useObservability>;
  errorLabel: string;
  routeTemplate: string;
  children: ReactNode;
};

function ErrorBoundaryWithObservability({
  children,
  errorLabel,
}: {
  children?: ReactNode;
  errorLabel?: string;
}) {
  const observability = useObservability();
  const { pathname } = useRouter();
  return (
    <ErrorBoundary
      observability={observability}
      errorLabel={errorLabel ?? 'cosmos'}
      routeTemplate={pathname}
    >
      {children}
    </ErrorBoundary>
  );
}

class ErrorBoundary extends React.Component<Props, State> {
  override state = { hasError: false };

  static getDerivedStateFromError(_error: unknown) {
    return { hasError: true };
  }

  private async reportError(error: unknown, errorInfo: ErrorInfo) {
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    const { observability, errorLabel, routeTemplate } = this.props;
    await initObservability();
    observability.trackError(
      errorLabel,
      normalizedError,
      {
        componentStack: errorInfo.componentStack,
        errorBoundary: 'react',
        errorName: normalizedError.name,
        errorSource: 'react-boundary',
        routeTemplate,
      },
      { skipSentry: true },
    );
  }

  override componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    void this.reportError(error, errorInfo);
    throw error;
  }

  override render(): React.ReactNode {
    // ErrorApp owns recovery UI after componentDidCatch rethrows.
    return this.state.hasError ? null : this.props.children;
  }
}

export default ErrorBoundaryWithObservability;
